import React, { useState, useEffect, useCallback, FunctionComponent, useRef } from 'react';

import { getCsrfToken } from '../../../libs/csrfToken';
import { scrollToTop } from '../../../libs/scrollUtils';
import { Presentation } from './presentation';
import { getFilteredCompanies, getCompanyIds } from '../../../services/companies';
import type { Props, SortBy } from './types';

export const Component: FunctionComponent<Props> = (props) => {
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [industryId, setIndustryId] = useState<string>(props.industryId || ''); // TODO
  const [industryIds, setIndustryIds] = useState<string[]>([]);
  const [selectedIndustry, setSelectedIndustry] = useState<number | null>(null);
  const [sortBy, setSortBy] = useState<SortBy>('default');
  const [page, setPage] = useState(1);
  const [initialCompanyIds, setInitialCompanyIds] = useState<number[]>(props.companyIds || []);
  const [companyIds, setCompanyIds] = useState<number[]>(props.companyIds || []);
  const [displayedCompanyIds, setDisplayedCompanyIds] = useState<number[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [filteredCompaniesCount, setFilteredCompaniesCount] = useState(0);

  const isInitialMount = useRef(true);

  const fetchFilteredCompanies = useCallback(
    async (override?: {
      q?: string;
      industryIds?: string[];
      page?: number;
      sortBy?: SortBy;
      selectedIndustry?: number | null; // 将来使いたければ
    }) => {
    setLoading(true);
    const csrfToken = getCsrfToken();
    const q0 = override?.q ?? q;
    const ids0 = override?.industryIds ?? industryIds;
    const page0 = override?.page ?? page;
    const sort0 = override?.sortBy ?? sortBy;
    try {
      const data = await getFilteredCompanies({
        csrfToken,
        body: JSON.stringify({
          q: q,
          industry_id: industryId,
          industry_ids: industryIds,
          page: page,
          per_page: 20,
          sort_by: sortBy,
        }),
      });
      setDisplayedCompanyIds(data.company_ids);
      setTotalCount(data.total_count);
      setFilteredCompaniesCount(data.total_count);
      setLoading(false);
      setIndustryId('');
    } catch (error) {
      console.error('Error fetching filtered companies:', error);
      setLoading(false);
    }
  }, [q, industryIds, page, sortBy]);

  const parseUrlParams = useCallback(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const newQ = searchParams.get('q') || '';
    const newIndustryIds = searchParams.get('industryIds')?.split(',') || [];
    const newSelectedIndustry = searchParams.get('selectedIndustry')
      ? parseInt(searchParams.get('selectedIndustry')!)
      : null;
    const newPage = searchParams.get('page') ? parseInt(searchParams.get('page')!) : 1;

    setQ((prevQ) => (prevQ !== newQ ? newQ : prevQ));

    setIndustryIds((prevIds) => {
      if (prevIds.length !== newIndustryIds.length || prevIds.some((id, i) => id !== newIndustryIds[i])) {
        return newIndustryIds;
      }
      return prevIds;
    });

    setSelectedIndustry((prev) => (prev !== newSelectedIndustry ? newSelectedIndustry : prev));
    setPage((prev) => (prev !== newPage ? newPage : prev));

    setSortBy((prevSortBy) => (prevSortBy !== 'default' ? 'default' : prevSortBy));
  }, []);

  const handleChangeUrl = useCallback(() => {
    const defaultUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
    const params = new URLSearchParams();

    if (q) params.append('q', q);
    if (industryIds.length > 0) params.append('industryIds', industryIds.join(','));
    if (selectedIndustry !== null) params.append('selectedIndustry', selectedIndustry.toString());
    if (page !== 1) params.append('page', page.toString());

    const queryString = params.toString();
    const url = queryString ? `${defaultUrl}?${queryString}` : defaultUrl;
    window.history.pushState({}, '', url);
  }, [q, industryIds, selectedIndustry, page]);

  const updateFilteredCount = useCallback(
    async (companyName: string, selectedIndustries: number[]) => {
      const csrfToken = getCsrfToken();
      try {
        const data = await getFilteredCompanies({
          csrfToken,
          body: JSON.stringify({
            q: companyName,
            industry_ids: selectedIndustries,
            page: 1,
            per_page: 20,
            sort_by: sortBy,
          }),
        });
        setFilteredCompaniesCount(data.total_count);
      } catch (error) {
        console.error('Error updating filtered count:', error);
      }
    },
    [sortBy]
  );

  const handleSearch = useCallback(
    (companyName: string, selectedIndustries: number[], shouldUpdateList: boolean) => {
      setQ(companyName);
      setIndustryIds(selectedIndustries.map(String));
      setPage(1);
      if (!shouldUpdateList) {
        updateFilteredCount(companyName, selectedIndustries);
      }
      scrollToTop(undefined, 0, 'smooth');
    },
    [updateFilteredCount]
  );

  const handleIndustryChange = useCallback((industryIdParam: number) => {
    setQ('');
    setIndustryIds([industryIdParam.toString()]);
    setPage(1);
    setSelectedIndustry(industryIdParam);
  }, []);

  const handleSortChange = useCallback(
    (sortOption: SortBy) => {
      setSortBy(prev => (prev === sortOption ? 'default' : sortOption));
      setPage(1);
      scrollToTop();
    },
    []
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (newPage === page) return;
      setLoading(true);
      scrollToTop();
      setPage(newPage);
    },
    [page]
  );

  const fetchCompaniesData = useCallback(async () => {
    try {
      const data = await getCompanyIds();
      setInitialCompanyIds(data.companyIds);
      setCompanyIds(data.companyIds);
    } catch (error) {
      console.error('Error fetching company IDs:', error);
    }
  }, []);

  useEffect(() => {
    parseUrlParams();
    window.addEventListener('popstate', parseUrlParams);

    if (props.showIndustryFilter) {
      if (props.industryId) {
        setIndustryId(String(props.industryId));
        setIndustryIds([String(props.industryId)]);
      }
      fetchCompaniesData();
    } else {
      setInitialCompanyIds(props.companyIds || []);
      setCompanyIds(props.companyIds || []);
    }

    fetchFilteredCompanies();

    return () => {
      window.removeEventListener('popstate', parseUrlParams);
    };
  }, [parseUrlParams, fetchCompaniesData, props.showIndustryFilter, props.companyIds, props.industryId]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    fetchFilteredCompanies();
  }, [q, industryIds, page, sortBy, fetchFilteredCompanies]);

  useEffect(() => {
    if (isInitialMount.current) {
      return;
    }
    handleChangeUrl();
  }, [q, industryIds, selectedIndustry, page, handleChangeUrl]);

  const totalPages = Math.ceil(totalCount / 20); // itemsPerPageを直接指定

  const handleModalOn = useCallback(() => {
    setLoading(true);
  }, []);

  useEffect(() => {
    const handlePop = () => {
      parseUrlParams();
      fetchFilteredCompanies();
    };

    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, [parseUrlParams, fetchFilteredCompanies]);

  const presentationProps = {
    ...props,
    loading,
    totalCount,
    filteredCompaniesCount,
    dataCategory: 'companylist',
    dataValue: {
      companiesSort: 'companies_sort',
      sortRecruitment: 'sort_recruitment',
      sortEvent: 'sort_event',
      sortStory: 'sort_story',
      industryLink: 'industry_link',
      companyName: 'company_name',
    },
    q,
    industryIds,
    selectedIndustry,
    displayedCompanyIds,
    initialCompanyIds,
    page,
    totalPages,
    itemsPerPage: 20,
    sortBy,
    handleSearch,
    fetchFilteredCompanies,
    handleIndustryChange,
    updateFilteredCount,
    handlePageChange,
    handleSortChange,
    handleModalOn,
  };

  return <Presentation {...presentationProps} />;
};
